import { animate, useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export interface UseAnimatedNumberOptions {
  durationMs?: number
  initialValue?: number
  isReady?: boolean
}

export const useAnimatedNumber = (
  value: number,
  { durationMs = 800, initialValue = 0, isReady = true }: UseAnimatedNumberOptions = {}
): number => {
  const initialDisplayValue = isReady && Number.isFinite(value) ? value : initialValue
  const [displayValue, setDisplayValue] = useState(initialDisplayValue)
  const previousValue = useRef(initialDisplayValue)
  const hasReadyValue = useRef(isReady && Number.isFinite(value))
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    if (!Number.isFinite(value)) return

    if (!isReady) return

    if (!hasReadyValue.current) {
      hasReadyValue.current = true
      previousValue.current = value
      setDisplayValue(value)
      return
    }

    const from = previousValue.current
    const to = value

    if (from === to || shouldReduceMotion) {
      previousValue.current = value
      setDisplayValue(value)
      return
    }

    const controls = animate(from, to, {
      duration: durationMs / 1000,
      ease: 'easeOut',
      onUpdate: (latestValue) => {
        previousValue.current = latestValue
        setDisplayValue(latestValue)
      },
      onComplete: () => {
        previousValue.current = to
        setDisplayValue(to)
      },
    })

    return () => controls.stop()
  }, [durationMs, isReady, shouldReduceMotion, value])

  return isReady && !hasReadyValue.current && Number.isFinite(value) ? value : displayValue
}
